/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, ReactNode, useCallback } from "react";
import classNames from "classnames/dedupe";
import {
  RadioButton,
  TRadioButtonProps,
} from "../../../ActionsSelectors/RadioButton";
import { Button } from "../../../ActionsSelectors/Button";
import { SvgIcon } from "../../../Icons";

export interface IOptionSummaryWidgetProps extends TRadioButtonProps {
  classes?: {
    root?: string;
    radio?: string;
    label?: string;
    buttonsWrapper?: string;
    button?: string;
    removeButton?: string;
  };

  background?: "grey" | "white";

  customTitle?: ReactNode;

  /** Text labels */
  labels?: {
    /** Title text */
    title?: string;
  };

  /** Changes some appearance for readonly-mode */
  readOnly?: boolean;

  /** Enable "edit" button */
  withEdit?: boolean;

  /** Enable "remove" button */
  withRemove?: boolean;

  /** Set if component will render `RadioButton` */
  withRadio?: boolean;

  /** Edit action callback */
  onEdit?: (event: React.MouseEvent, id: string) => void;

  /** Remove action callback */
  onRemove?: (event: React.MouseEvent, id: string) => void;
}

const OptionSummaryWidget: FC<IOptionSummaryWidgetProps> = ({
  classes,
  background = "grey",
  labels,
  customTitle,
  name,
  id,
  withRadio = false,
  withEdit = true,
  withRemove = false,
  readOnly,
  onEdit,
  onRemove,
  children,
  ...other
}) => {
  const handleEdit = useCallback(
    (event: React.MouseEvent) => {
      onEdit && onEdit(event, id);
    },
    [id, onEdit],
  );

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      onRemove && onRemove(event, id);
    },
    [id, onRemove],
  );

  const rootClasses = classNames(
    "py-7 px-6",
    { "bg-color-grey-200": background === "grey" },
    { "bg-color-white": background === "white" },
    classes?.root,
  );

  return (
    <div className={rootClasses} {...other}>
      <div className="d-flex flex-grow-1">
        {withRadio && (
          <RadioButton
            className={classNames(
              `form-check-input form-check-input--light flex-shrink-0`,
              classes?.radio,
              {
                "cursor-pointer": !readOnly,
              },
            )}
            name={name}
            id={id}
            value={id}
            disabled={readOnly}
          />
        )}

        <label
          className={classNames(
            "flex-grow-1 min-w-0",
            {
              "ms-5": withRadio,
              "cursor-pointer": !readOnly,
            },
            classes?.label,
          )}
          htmlFor={readOnly ? undefined : id}
        >
          {customTitle || (
            <div className="d-header-14-bold text-color-blue">
              {labels?.title}
            </div>
          )}
        </label>

        {(withEdit || withRemove) && (
          <div
            className={
              classes?.buttonsWrapper ??
              "d-flex flex-column align-self-stretch justify-content-between"
            }
          >
            {withEdit && (
              <Button
                data-testid="optionsummary-edit"
                variant="icon"
                classes={{
                  root: `border-0 bg-transparent box-shadow-none text-color-red-hover ${
                    classes?.button || ""
                  }`,
                }}
                style={{
                  marginTop: "-10px",
                }}
                onClick={handleEdit}
              >
                <SvgIcon type="svg-icon" icon="EditIcon" />
              </Button>
            )}

            {withRemove && (
              <Button
                data-testid="optionsummary-remove"
                variant="icon"
                classes={{
                  root: `border-0 bg-transparent box-shadow-none text-color-red-hover ${
                    classes?.removeButton || ""
                  }`,
                }}
                onClick={handleRemove}
              >
                <SvgIcon type="svg-icon" icon="DeleteIcon" />
              </Button>
            )}
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

export default OptionSummaryWidget;
